import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaCalendarAlt, FaClock } from 'react-icons/fa';
import SchedulingCalendarView from './SchedulingCalendarView';
import { scheduleEstimate, getEstimate, getEstimates } from '../../services/estimateService';
import './ScheduleEstimateModal.css';

const DEFAULT_FORM = {
  scheduled_date: '',
  scheduled_time: '09:00',
  duration: 60,
  estimator_name: 'Brett',
  notes: '',
};

const DURATION_OPTIONS = [30, 60, 90, 120, 180];

const TIME_OPTIONS = [
  '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
  '19:00', '19:30', '20:00', '20:30',
];

const toAmPm = (time24) => {
  const [hStr, mStr] = time24.split(':');
  const h = parseInt(hStr, 10);
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour12}:${mStr} ${ampm}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

const ScheduleEstimateModal = ({ show, estimateId, onClose, onScheduled }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [estimate, setEstimate] = useState(null);
  const [existingEstimates, setExistingEstimates] = useState([]);

  const minDate = useMemo(() => todayISO(), []);

  const reset = useCallback((keepNotes = false) => {
    setFormData(prev => (keepNotes ? { ...DEFAULT_FORM, notes: prev.notes } : DEFAULT_FORM));
    setError('');
  }, []);

  // Load estimate + background calendar items when modal opens
  const loadData = useCallback(async () => {
    try {
      const [estimateData, estimates] = await Promise.all([
        getEstimate(estimateId),
        getEstimates().catch(() => []), // calendar conflicts are "nice-to-have"
      ]);

      setEstimate(estimateData);
      // Keep the current estimate out of the background list if present
      setExistingEstimates((estimates || []).filter(e => e.id !== estimateId));
    } catch (err) {
      console.error('Error loading estimate:', err);
      setError('Failed to load estimate details');
    }
  }, [estimateId]);

  useEffect(() => {
    if (!show || !estimateId) return;

    reset(false);
    loadData();
  }, [show, estimateId, loadData, reset]);

  const setField = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleDateChange = useCallback((e) => {
    setField('scheduled_date', e.target.value);
  }, [setField]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setField(name, value);
  }, [setField]);

  const handleCalendarSlotSelect = useCallback(({ start, duration }) => {
    if (!start) return;

    // Local extraction to avoid timezone surprises
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    const hh = String(start.getHours()).padStart(2, '0');
    const mm = String(start.getMinutes()).padStart(2, '0');
    const timeStr = `${hh}:${mm}`;

    setFormData(prev => ({
      ...prev,
      scheduled_date: dateStr,
      scheduled_time: TIME_OPTIONS.includes(timeStr) ? timeStr : '09:00',
      duration: duration || prev.duration,
    }));
  }, []);

  const clearSelection = useCallback(() => {
    reset(true); // keep notes
  }, [reset]);

  const handleClose = useCallback(() => {
    reset(false);
    setEstimate(null);
    setExistingEstimates([]);
    onClose?.();
  }, [onClose, reset]);

  const getEstimateDisplayInfo = useMemo(() => {
    if (!estimate) return '';
    const parts = [];
    if (estimate.customer_name) parts.push(estimate.customer_name);
    if (estimate.site_name) parts.push(estimate.site_name);
    if (estimate.site_address) parts.push(estimate.site_address);
    return parts.join(' • ');
  }, [estimate]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.scheduled_date) throw new Error('Please select a date for the estimate');

      const schedulingData = {
        schedule_date: formData.scheduled_date,
        schedule_time: formData.scheduled_time,
        duration: parseInt(formData.duration, 10),
        estimator_name: formData.estimator_name,
        notes: formData.notes,
      };

      const scheduledEstimate = await scheduleEstimate(estimateId, schedulingData);

      onScheduled?.(scheduledEstimate);
      handleClose();
    } catch (err) {
      console.error('Error scheduling estimate:', err);
      setError(err.message || 'Failed to schedule estimate');
    } finally {
      setLoading(false);
    }
  }, [estimateId, formData, onScheduled, handleClose]);

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <Modal.Body className="schedule-estimate-modal">
        {estimate && (
          <div className="schedule-estimate-modal__header alert alert-info mb-3 position-relative">
            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 m-2"
              onClick={handleClose}
              aria-label="Close"
            />
            <h5 className="mb-2">
              <FaCalendarAlt className="me-2" />
              Schedule Estimate #{estimateId}
            </h5>
            <strong>Title:</strong> {estimate.title}
            <br />
            <strong>Location:</strong> {getEstimateDisplayInfo}
          </div>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Row className="g-3">
          {/* Calendar */}
          <Col md={7}>
            <h6>Select Date &amp; Time</h6>
            <p className="text-muted small">
              Click a time slot in the calendar or use the fields to schedule your estimate.
            </p>

            <SchedulingCalendarView
              estimates={existingEstimates}
              onSelectSlot={handleCalendarSlotSelect}
              selectedSlot={formData.scheduled_date ? { date: formData.scheduled_date } : null}
            />
          </Col>

          {/* Form */}
          <Col md={5}>
            <Form onSubmit={handleSubmit}>
              <h6>Schedule Details</h6>

              <Form.Group className="mb-3">
                <Form.Label>
                  <FaCalendarAlt className="me-1" /> Date *
                </Form.Label>
                <Form.Control
                  type="date"
                  name="scheduled_date"
                  value={formData.scheduled_date}
                  onChange={handleDateChange}
                  required
                  min={minDate}
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  <FaClock className="me-1" /> Time *
                </Form.Label>
                <Form.Select
                  name="scheduled_time"
                  value={formData.scheduled_time}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  {TIME_OPTIONS.map(t => (
                    <option key={t} value={t}>
                      {toAmPm(t)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Duration</Form.Label>
                <Form.Select
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {DURATION_OPTIONS.map(mins => (
                    <option key={mins} value={mins}>
                      {mins === 60 ? '1 hour' : mins === 30 ? '30 minutes' : `${mins / 60} hours`}
                    </option>
                  ))}

                  {/* Preserve custom duration */}
                  {formData.duration &&
                    !DURATION_OPTIONS.includes(parseInt(formData.duration, 10)) && (
                      <option value={formData.duration}>
                        {(formData.duration / 60).toFixed(1)} hours ({formData.duration} minutes)
                      </option>
                    )}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Estimator</Form.Label>
                <Form.Control
                  type="text"
                  name="estimator_name"
                  value={formData.estimator_name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Optional scheduling notes..."
                  disabled={loading}
                />
              </Form.Group>

              {formData.scheduled_date && (
                <div className="mb-3">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={clearSelection}
                    disabled={loading}
                    className="w-100"
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              <div className="d-grid gap-2">
                <Button type="submit" variant="primary" disabled={loading || !formData.scheduled_date}>
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Scheduling...
                    </>
                  ) : (
                    'Schedule Estimate'
                  )}
                </Button>

                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    border: 'none',
                    backgroundColor: '#6c757d',
                    color: 'white'
                  }}
                >
                  Skip for Now
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default ScheduleEstimateModal;
